import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Users, Headphones, Sprout, MapPin, Calendar } from "lucide-react";
import { useLang } from "@/i18n";
import { useFetch } from "@/hooks/useFetch";
import { Reveal } from "@/components/Reveal";
import Countdown from "@/components/Countdown";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9aaee211-f63c-4a28-8edc-d5bfaad8b13c/images/de17eb1b2aa7b8e76748470ec1c18fa1e2e109b377c70f881a2c34734fe1662c.jpeg";
const IMG_CELEBRATION = "https://static.prod-images.emergentagent.com/jobs/9aaee211-f63c-4a28-8edc-d5bfaad8b13c/images/789c70ad6cac1f6b77539bb4a6d760d63fb73d2e27cdf905f61d356a36819a76.jpeg";
const IMG_HERITAGE = "https://static.prod-images.emergentagent.com/jobs/9aaee211-f63c-4a28-8edc-d5bfaad8b13c/images/a5c9bdb10b889b4f1941e301e647fc430f7ed5a37a79a4330232c88a0abcbaca.jpeg";
const IMG_PEOPLES = "https://commons.wikimedia.org/wiki/Special:FilePath/Traditional_Attire_Ewondo.jpg?width=1280";
const IMG_COCOA = "https://static.prod-images.emergentagent.com/jobs/9aaee211-f63c-4a28-8edc-d5bfaad8b13c/images/3bd9ca76e947c98055815e10931f55fdc551def6b6b8cf8699f519998b9b282c.jpeg";

const DOORS = [
  { to: "/histoire", icon: Compass, titleKey: "door_history", subKey: "door_history_sub", img: IMG_HERITAGE },
  { to: "/peuples", icon: Users, titleKey: "door_peoples", subKey: "door_peoples_sub", img: IMG_PEOPLES },
  { to: "/contes", icon: Headphones, titleKey: "door_memory", subKey: "door_memory_sub", img: IMG_CELEBRATION },
  { to: "/projets", icon: Sprout, titleKey: "door_future", subKey: "door_future_sub", img: IMG_COCOA },
];

const Home = () => {
  const { t, tr } = useLang();
  const { data: periods } = useFetch("/history/periods");
  const { data: peoples } = useFetch("/peoples");
  const { data: projects } = useFetch("/projects");
  const { data: events } = useFetch("/events");

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[620px] flex items-end overflow-hidden ab-grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Ancien Beti-Ekang jouant du mvet" className="w-full h-full object-cover animate-slow-zoom" />
          {/* Strong scrims so the title is always perfectly legible */}
          <div className="absolute inset-0 bg-gradient-to-r from-ab-black via-ab-black/85 to-ab-black/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ab-black via-ab-black/40 to-transparent" />
          <div className="absolute inset-0 bg-ab-black/25" />
        </div>
        <div className="ab-container relative pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="inline-flex items-center gap-2 rounded-full border border-ab-gold/40 bg-ab-black/50 backdrop-blur-xl backdrop-saturate-150 px-4 py-1.5 mb-7"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ab-gold" />
            <span className="text-ab-gold text-[0.7rem] sm:text-xs tracking-[0.2em] uppercase">{t("devise")}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.15 }}
            className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-ab-ivory max-w-4xl drop-shadow-[0_4px_30px_rgba(0,0,0,0.9)]"
          >
            {t("hero_enter")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }}
            className="mt-8 text-ab-ivory/85 text-base md:text-lg max-w-2xl leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]"
          >
            {t("subtitle")}.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/histoire" className="ab-btn-primary" data-testid="hero-explore-history">
              {t("door_history")} <ArrowRight size={16} />
            </Link>
            <Link to="/adhesion" className="ab-btn-ghost" data-testid="hero-join">
              {t("nav_join")}
            </Link>
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2" aria-hidden="true" data-testid="hero-scroll-hint">
          <span className="text-[0.6rem] tracking-[0.35em] uppercase text-ab-ivory/50">{tr({ fr: "Défiler", en: "Scroll" })}</span>
          <span className="w-px h-10 bg-gradient-to-b from-ab-gold/70 to-transparent ab-scroll-hint" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 ab-hairline-gold" aria-hidden="true" />
      </section>

      {/* BIENVENUE / MANIFESTE */}
      <section className="py-20 md:py-24 bg-ab-forest/20 border-b border-ab-gold/10" data-testid="home-welcome">
        <div className="ab-container max-w-4xl text-center">
          <Reveal>
            <p className="ab-eyebrow mb-4">Bienvenue · Mbembe Nsuan</p>
            <h2 className="font-serif text-3xl md:text-5xl text-ab-ivory leading-tight">
              Ensemble pour l'unité, la solidarité et le développement de la communauté Beti
            </h2>
            <p className="mt-6 text-ab-ivory/70 leading-relaxed max-w-2xl mx-auto">
              ANYANG BETI n'est pas un simple groupe de discussion : c'est un cadre de rassemblement, d'entraide
              et de développement. Ne demandons pas seulement ce que la communauté peut faire pour nous —
              demandons-nous ce que nous pouvons faire pour elle.
            </p>
            <Link to="/manifeste" className="mt-8 inline-flex items-center gap-2 text-ab-gold ab-link-underline text-sm tracking-widest uppercase" data-testid="home-manifeste-link">
              Lire notre manifeste <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* COMPTE À REBOURS OLYMPIADES 2027 */}
      <Countdown />

      {/* 4 DOORS */}
      <section className="border-t border-ab-gold/10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {DOORS.map((d, i) => {
            const Icon = d.icon;
            return (
              <Link
                key={d.to}
                to={d.to}
                data-testid={`door-${i}`}
                className="group relative h-[46vh] md:h-[68vh] overflow-hidden border-b lg:border-b-0 lg:border-r border-ab-gold/10 last:border-r-0"
              >
                <img
                  src={d.img} alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-ab-black/45 group-hover:bg-ab-black/25 transition-colors duration-700" />
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <Icon className="text-ab-gold mb-4" size={26} />
                  <h3 className="font-serif text-2xl md:text-3xl text-ab-ivory leading-tight">{t(d.titleKey)}</h3>
                  <p className="mt-3 text-ab-ivory/70 text-sm leading-relaxed max-w-xs">{t(d.subKey)}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-ab-gold text-xs tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {t("explore")} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CHRONOLOGIE SYNTHÉTIQUE (periods) */}
      <section className="py-24 md:py-32 bg-ab-ebony border-y border-ab-gold/10">
        <div className="ab-container">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="ab-eyebrow mb-3">{t("nav_history")}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-ab-ivory">{tr({ fr: "Les grandes périodes", en: "The great periods" })}</h2>
            </div>
            <Link to="/histoire" className="hidden md:inline-flex items-center gap-2 text-ab-gold text-sm tracking-widest uppercase ab-link-underline">
              {t("all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {periods?.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/histoire/${p.slug}`} className="group block ab-card overflow-hidden h-full">
                  <div className="aspect-video overflow-hidden">
                    <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <span className="text-ab-gold text-xs tracking-widest uppercase">{tr(p.period)}</span>
                    <h3 className="font-serif text-2xl text-ab-ivory mt-2 leading-tight">{tr(p.title)}</h3>
                    <p className="mt-3 text-ab-ivory/60 text-sm line-clamp-3">{tr(p.summary)}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PEUPLES */}
      <section className="py-24">
        <div className="ab-container">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="ab-eyebrow mb-3">{t("nav_peoples")}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-ab-ivory">{tr({ fr: "Peuples et ramifications", en: "Peoples and ramifications" })}</h2>
            </div>
            <Link to="/peuples" className="hidden md:inline-flex items-center gap-2 text-ab-gold text-sm tracking-widest uppercase ab-link-underline">
              {t("all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {peoples?.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link to={`/peuples/${p.slug}`} className="group relative aspect-[3/4] overflow-hidden ab-hairline block">
                  <img src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ab-black via-ab-black/40 to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <h3 className="font-serif text-xl text-ab-ivory">{tr(p.name)}</h3>
                    <p className="text-ab-gold text-[0.7rem] tracking-wider uppercase">{p.family}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROJETS + ÉVÉNEMENTS */}
      <section className="py-20 bg-ab-ebony border-y border-ab-gold/10">
        <div className="ab-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p className="ab-eyebrow mb-3">{t("nav_projects")}</p>
            <h2 className="font-serif text-3xl text-ab-ivory mb-8">Bâtir notre avenir</h2>
            <div className="space-y-4">
              {projects?.slice(0, 3).map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <Link to="/projets" className="group flex gap-5 items-center ab-hairline bg-ab-wood/10 hover:bg-ab-wood/30 transition-colors p-4">
                    <img src={p.image} alt="" className="w-24 h-24 object-cover shrink-0" />
                    <div className="flex-1">
                      <span className="text-ab-gold text-[0.7rem] tracking-widest uppercase">{tr(p.category)}</span>
                      <h3 className="font-serif text-xl text-ab-ivory">{tr(p.title)}</h3>
                      <div className="mt-2 h-1 bg-ab-black/60 w-full">
                        <div className="h-full bg-ab-gold" style={{ width: `${p.progress}%` }} />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <p className="ab-eyebrow mb-3">{t("nav_events")}</p>
            <h2 className="font-serif text-3xl text-ab-ivory mb-8">{tr({ fr: "Prochains rendez-vous", en: "Upcoming events" })}</h2>
            <div className="space-y-4">
              {events?.slice(0, 3).map((e, i) => (
                <Reveal key={e.slug} delay={i * 0.08}>
                  <div className="ab-hairline bg-ab-black/40 p-5">
                    <div className="flex items-center gap-2 text-ab-gold text-xs">
                      <Calendar size={13} /> {typeof e.date === "string" ? e.date : tr(e.date)}
                    </div>
                    <h3 className="font-serif text-lg text-ab-ivory mt-2">{tr(e.title)}</h3>
                    <p className="flex items-center gap-1 text-ab-ivory/50 text-xs mt-1"><MapPin size={12} /> {tr(e.location)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA ADHÉSION */}
      <section className="relative py-28 overflow-hidden ab-grain">
        <img src={IMG_CELEBRATION} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-ab-black/80" />
        <div className="ab-container relative text-center max-w-3xl">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-ab-ivory">{t("join_cta_title")}</h2>
            <p className="mt-6 text-ab-ivory/70 leading-relaxed">{t("join_cta_text")}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/adhesion" className="ab-btn-primary" data-testid="cta-join">{t("nav_join")} <ArrowRight size={16} /></Link>
              <Link to="/contes" className="ab-btn-ghost">{t("door_memory")}</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
